import { Component, Input, OnInit } from '@angular/core';
import { CustomerSelectionOption } from '@app/customer/models/customer-selection-option.model';
import { CustomerService } from '@app/customer/services/customer.service';
import { ModalController } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ShipToModalSelections } from '@order/models/order-view-model';
import { OrderConversionService } from '@order/services/order-conversion.service';
import { PlatformService, SelectedCustomerState } from '@panamax/app-state';
import { Department, Customer } from '@usf/customer-types';
import { CustomerSummary } from '@usf/customer-types/customer';
import { DepartmentSummary } from '@usf/customer-types/department';
import dayjs from 'dayjs';
import { filter, map, of, take } from 'rxjs';
@Component({
  selector: 'app-ship-to-modal',
  templateUrl: './ship-to-modal.component.html',
  styleUrls: ['./ship-to-modal.component.scss'],
})
export class ShipToModalComponent implements OnInit {
  searchKey = '';
  currentDivision = 0;

  filteredCustomerList: Customer[];
  superUserCustomerList$ = (
    this.customerService?.getFilteredCustomerSummaries$() || of([])
  ).pipe(map(summaries => this.getFilteredSuperUserCustomerList(summaries)));
  superUserCustomerListLoading$ =
    this.customerService.getCustomersDataLoadingSpinner$();
  checkedCustomerNumber: number;
  checkedDepartmentNumber: number;
  superUserSearchPerformed = false;
  isCustomerLoadingSuperUser = false;

  currentDeliveryDate: string; //current delivery date on the order with timezone removed
  deliveryDateManuallySelected: boolean;
  nextYear: string; //used for maxDate on ion-datetime component; otherwise can only select current year

  currentStep = 1;
  currentSelections: ShipToModalSelections;
  poRegex = new RegExp('^[-0-9a-zA-Z ]{1,20}$');

  selectedCustomer: CustomerSelectionOption;
  currentPage = 1;
  @Input() initialDeliveryDateForOrder: string | Date; //initial delivery date on order
  @Input() customers: Customer[];
  @Input() currentCustomer: SelectedCustomerState; //customer currently in state
  @Input() currentShipToCustomerNumber: number; //current shipToCustomer on the order
  @Input() currentShipToDepartmentNumber: number; //current shipToDepartment on the order
  @Input() currentPurchaseOrderNumber: string; //initial order po#
  @Input() isSuperUser: boolean; //initial order po#

  constructor(
    protected modalController: ModalController,
    protected translateService: TranslateService,
    private store: Store,
    readonly platformService: PlatformService,
    readonly customerService: CustomerService,
    private orderConversionService: OrderConversionService,
  ) {}

  ngOnInit() {
    this.resetSearch();
    this.currentStep = 1;
    this.currentDeliveryDate = this.initialDeliveryDateForOrder
      .toString()
      .split('T')[0];
    this.nextYear = (new Date().getFullYear() + 1).toString();
    this.customerService.setFilteredCustomerSummaries([]);
    this.superUserSearchPerformed = false;
  }

  customerSearch() {
    this.filteredCustomerList = this.customers.filter(
      customer =>
        (customer?.customerName
          .toLowerCase()
          .includes(this.searchKey?.toLowerCase()) ||
          customer?.customerNumber?.toString().includes(this.searchKey)) &&
        (customer?.departmentNumbers?.length === 0
          ? this.shouldDisplayCustomer(customer?.customerNumber, false)
          : customer),
    );
  }
  superUserCustomerSearch(): void {
    this.customerService.setShowCustomersDataLoadingSpinner(true);
    this.superUserSearchPerformed = true;
    this.currentDivision = this?.currentCustomer?.divisionNumber;

    let searchKey = this.searchKey;
    if (searchKey && searchKey.length >= 3 && this.currentDivision !== 0) {
      return this.customerService.searchCustomers(this.searchKey);
    }
  }

  handleCustomerSelection(
    event: any,
    selectedCustomer: Customer | CustomerSummary,
    department: Department | DepartmentSummary,
  ) {
    const customer = selectedCustomer as Customer;

    const customerDeliveryDays = this.getCustomerDeliveryDays(customer);

    const nextCustomerDeliveryDate = this.getNextCustomerDeliveryDate();
    //If delivery date is invalid for the new customer, set the delivery date to customer's next delivery day.
    let newDeliveryDate = this.isDateEnabled(this.currentDeliveryDate)
      ? this.currentDeliveryDate
      : nextCustomerDeliveryDate;

    this.deliveryDateManuallySelected = false;

    this.currentSelections = {
      isCustomerSelected: event.detail.checked,
      customer: event.detail.checked ? selectedCustomer : null,
      department: event.detail.checked ? department : null,
      nextCustomerDeliveryDate: event.detail.checked
        ? nextCustomerDeliveryDate
        : null,
      customerDeliveryDays,
      updatedDeliveryDate: event.detail.checked ? newDeliveryDate : null,
      poNumber: this.currentPurchaseOrderNumber || '',
    };
  }

  handleSuperUserCustomerSelection(
    event: any,
    customerSummary: CustomerSummary,
    department: DepartmentSummary,
  ): void {
    if (!event.detail.checked) {
      this.currentSelections.isCustomerSelected = false;
      this.currentSelections.customer = null;
      this.checkedCustomerNumber = null;
      return;
    }
    if (
      this.checkedCustomerNumber === customerSummary?.customerNumber &&
      this.checkedDepartmentNumber !== Number(department.departmentNumber)
    ) {
      this.checkedDepartmentNumber = Number(department?.departmentNumber);
      return this.handleCustomerSelection(
        event,
        this.currentSelections.customer,
        department,
      );
    }
    if (
      (this.checkedCustomerNumber !== customerSummary?.customerNumber ||
        (this.checkedCustomerNumber === customerSummary?.customerNumber &&
          this.checkedDepartmentNumber !==
            Number(department.departmentNumber))) &&
      event.detail.checked
    ) {
      this.isCustomerLoadingSuperUser = true;
      this.orderConversionService
        .getCustomerForSuperUser$(
          customerSummary?.customerNumber,
          customerSummary?.divisionNumber,
        )
        .pipe(
          filter(
            c =>
              c.customerNumber === customerSummary?.customerNumber &&
              c.divisionNumber === customerSummary?.divisionNumber,
          ),
          take(1),
        )
        .subscribe(customer => {
          this.checkedCustomerNumber = customer?.customerNumber;
          this.checkedDepartmentNumber = Number(department?.departmentNumber);
          this.isCustomerLoadingSuperUser = false;
          return this.handleCustomerSelection(event, customer, department);
        });
    }
  }

  resetSearch() {
    this.searchKey = '';
    this.filteredCustomerList = this.customers?.filter(customer => {
      return customer?.departmentNumbers?.length === 0
        ? this.shouldDisplayCustomer(customer?.customerNumber, false)
        : customer;
    });
  }

  shouldDisplayCustomer(
    customerNumber: number,
    isDepartment: boolean,
    departmentNumber?: number | string,
  ): boolean {
    if (!isDepartment) {
      return (
        this?.currentShipToCustomerNumber !== customerNumber &&
        this?.currentCustomer?.customerNumber !== customerNumber
      );
    } else if (
      !!this?.currentShipToDepartmentNumber &&
      this.currentShipToCustomerNumber === customerNumber
    ) {
      return (
        departmentNumber?.toString() !==
        this?.currentShipToDepartmentNumber?.toString()
      );
    } else return true;
  }

  getNextCustomerDeliveryDate(): string {
    const today = dayjs().startOf('day');
    const currentWeekday = today.day();
    let nextDeliveryDay = this.currentSelections?.customerDeliveryDays.find(
      day => day > currentWeekday,
    );
    if (nextDeliveryDay === undefined) {
      nextDeliveryDay = this.currentSelections?.customerDeliveryDays[0];
    }
    const daysAhead = (nextDeliveryDay - currentWeekday + 7) % 7 || 7;
    const nextDeliveryDate = today.add(daysAhead, 'day');

    return nextDeliveryDate.format('YYYY-MM-DD');
  }

  getCustomerDeliveryDays(
    customer: Customer | SelectedCustomerState,
  ): number[] {
    const days = new Array(
      customer?.sunDelivery,
      customer?.monDelivery,
      customer?.tueDelivery,
      customer?.wedDelivery,
      customer?.thuDelivery,
      customer?.friDelivery,
      customer?.satDelivery,
    );

    const deliveryDays = days.reduce(
      (accumulator, currentValue, currentIndex) => {
        if (currentValue) {
          accumulator.push(currentIndex);
        }
        return accumulator;
      },
      [],
    );
    this.currentSelections = {
      ...this.currentSelections,
      customerDeliveryDays: deliveryDays,
    };
    return deliveryDays;
  }

  isDateEnabled = (dateString: string) => {
    let isDateEnabled = true;
    const today = dayjs().startOf('day').format('YYYY-MM-DD');
    if (today === dateString) {
      isDateEnabled = false;
    }
    const date = new Date(dateString);
    const utcDay = date.getUTCDay();

    isDateEnabled =
      this?.currentSelections?.customerDeliveryDays?.includes(utcDay) &&
      dateString > today &&
      dateString != this.initialDeliveryDateForOrder;
    return isDateEnabled;
  };

  handleDateSelection($event: any) {
    const selectedDate = $event.detail.value;
    if (selectedDate === undefined) {
      return;
    } else if (this.isDateEnabled(selectedDate)) {
      this.currentSelections.updatedDeliveryDate = selectedDate;
      this.deliveryDateManuallySelected = true;
    }
  }

  updatePoNumber(event) {
    this.currentSelections = {
      ...this.currentSelections,
      poNumber: event.target.value,
    };
  }
  updateAddtlInstructions(event) {
    this.currentSelections = {
      ...this.currentSelections,
      additionalDeliveryIntructions: event.target.value,
    };
  }

  // Modal navigation:

  proceedToNextStep() {
    const stepOneComplete = this?.currentSelections?.isCustomerSelected;
    switch (this.currentStep) {
      case 1:
        if (stepOneComplete) {
          this.currentStep++;
        }
        break;
      case 2:
        if (stepOneComplete) {
          this.currentStep++;
        }
        break;
      case 3:
        this.saveAndCloseModal();
        break;
    }
  }

  saveAndCloseModal() {
    const isDeliveryDateOverride =
      this.currentDeliveryDate !== this.currentSelections?.updatedDeliveryDate;
    this.currentSelections = {
      ...this.currentSelections,
      isDeliveryDateOverride,
      updatedDeliveryDate: new Date(
        this.currentSelections.updatedDeliveryDate,
      ).toISOString(),
    };
    this.dismissModal(true);
  }

  isNextButtonDisabled(): boolean {
    return !(
      (this.currentStep === 1 &&
        this.currentSelections?.isCustomerSelected === true) ||
      (this.currentStep === 2 &&
        !!this.currentSelections?.customer &&
        !!this.currentSelections?.updatedDeliveryDate) ||
      this.currentStep === 3
    );
  }

  dismissModal(saveSelections: boolean) {
    this.modalController.dismiss({
      currentSelections: saveSelections ? this.currentSelections : null,
    });
  }

  changePage(pageNumber: number) {
    this.currentPage = pageNumber;
  }

  getFilteredSuperUserCustomerList(list: CustomerSummary[]): CustomerSummary[] {
    return list.filter(summary => {
      const isSameDivision =
        (summary?.divisionNumber || 0) ===
        (this?.currentCustomer?.divisionNumber || 0);

      const isNotCurrentCustomer = !!(
        this.currentCustomer?.departmentNumber * 1
      )
        ? true
        : this.currentCustomer?.customerNumber !== summary.customerNumber;

      const shouldDisplay = !this.currentShipToDepartmentNumber
        ? this.shouldDisplayCustomer(summary.customerNumber, false)
        : true;

      return isSameDivision && shouldDisplay && isNotCurrentCustomer;
    });
  }

  get showDeliveryDateChangedMessage(): boolean {
    return (
      this.currentSelections?.updatedDeliveryDate !==
        this.currentDeliveryDate && !this.deliveryDateManuallySelected
    );
  }

  get stepNameMobile(): string {
    if (this.currentStep === 1) {
      return 'i18n.orderSubmittedPage.shipToModal.step1Mobile';
    }
    if (this.currentStep === 2) {
      return 'i18n.orderSubmittedPage.shipToModal.step2Mobile';
    }
    if (this.currentStep === 3) {
      return 'i18n.orderSubmittedPage.shipToModal.step3Mobile';
    }
  }
}
